import { useCallback, useEffect, useRef } from 'react';
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from 'react-native';
import type { TTourAnchorRect } from '../types';
import { registerTourScrollView } from '../utils/viewport';

export const useTourScrollView = () => {
  const ref = useRef<ScrollView>(null);
  const offsetY = useRef(0);
  const viewportHeight = useRef(0);
  const contentHeight = useRef(0);

  useEffect(
    () =>
      registerTourScrollView({
        scrollBy: dy => {
          const maxOffset = Math.max(0, contentHeight.current - viewportHeight.current);

          ref.current?.scrollTo({
            y: Math.min(Math.max(0, offsetY.current + dy), maxOffset),
            animated: true,
          });
        },
        measureViewport: () =>
          new Promise<TTourAnchorRect | null>(resolve => {
            const node = ref.current?.getNativeScrollRef();

            if (!node) {
              resolve(null);
              return;
            }

            node.measureInWindow((x, y, width, height) =>
              resolve(height > 0 ? { x, y, width, height } : null),
            );
          }),
      }),
    [],
  );

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    offsetY.current = event.nativeEvent.contentOffset.y;
  }, []);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    viewportHeight.current = event.nativeEvent.layout.height;
  }, []);

  const onContentSizeChange = useCallback((_width: number, height: number) => {
    contentHeight.current = height;
  }, []);

  return { ref, onScroll, onLayout, onContentSizeChange, scrollEventThrottle: 16 } as const;
};
